// Cookiebot consent helpers.
//
// Cookiebot runs in manual blocking mode (see _document.tsx), so anything
// non-essential (Firebase persistence, Sentry Replay, GTM) must be gated manually
// with these helpers. Cookiebot is only loaded in production
// (see _document.tsx), so when it is absent (dev/staging) we treat everything as
// consented, otherwise the app would break outside production.

export type ConsentCategory = "preferences" | "statistics" | "marketing"

interface CookiebotConsent {
  necessary: boolean
  preferences: boolean
  statistics: boolean
  marketing: boolean
}

interface CookiebotGlobal {
  consent: CookiebotConsent
  consented: boolean
}

declare global {
  interface Window {
    Cookiebot?: CookiebotGlobal
  }
}

/**
 * Whether the user has consented to a given category.
 * Returns false during SSR, and true when Cookiebot is not present (non-prod).
 */
export const hasConsent = (category: ConsentCategory): boolean => {
  if (typeof window === "undefined") return false

  const cookiebot = window.Cookiebot
  if (!cookiebot) return true

  return Boolean(cookiebot.consent?.[category])
}

/**
 * True when consent state is available: either Cookiebot is absent (non-prod) or
 * the user has already responded to the dialog.
 */
export const isConsentReady = (): boolean => {
  if (typeof window === "undefined") return false

  return !window.Cookiebot || Boolean(window.Cookiebot.consented)
}

/**
 * Subscribe to consent becoming ready / changing. The callback is invoked
 * immediately when consent is already available (the Cookiebot event may have
 * fired before this listener was attached). Returns an unsubscribe function.
 */
export const onConsentChange = (callback: () => void): (() => void) => {
  if (typeof window === "undefined") return () => {}

  if (isConsentReady()) callback()

  const handler = () => callback()
  window.addEventListener("CookiebotOnConsentReady", handler)
  window.addEventListener("CookiebotOnAccept", handler)
  window.addEventListener("CookiebotOnDecline", handler)

  return () => {
    window.removeEventListener("CookiebotOnConsentReady", handler)
    window.removeEventListener("CookiebotOnAccept", handler)
    window.removeEventListener("CookiebotOnDecline", handler)
  }
}